/**
 * Branding Settings Tab
 * Manages visual identity (logo, colors, payment instructions)
 * + Portal Theme section (portal_management flag required)
 */
import { useState, useEffect, useRef } from 'react';
import { useBrandingSettings, useUpdateBrandingSettings, usePortalTheme, useUpdatePortalTheme } from '@/hooks';
import { useFeature } from '@/lib/features/FeatureProvider';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Palette, Save, AlertCircle, Upload, X, Image as ImageIcon, Globe } from 'lucide-react';
import { HexColorPicker } from 'react-colorful';
import { ImageUpload } from '@/components/ui/ImageUpload';
import type { BrandingSettings } from '@/lib/schemas';
import { DEFAULT_THEME } from '@/lib/theme';
import type { TenantTheme } from '@/lib/theme';

const FONT_OPTIONS = [
  { value: 'system-ui', label: 'System UI (default)' },
  { value: 'Inter', label: 'Inter' },
  { value: 'Roboto', label: 'Roboto' },
];

export function BrandingSettingsTab() {
  // Read from tenant feature flags (canonical source) — NOT user.modules.
  // The two systems can drift, which previously caused silent 403s on the
  // GET /api/me/settings/theme call. See docs/patterns/BACKEND_PATTERNS.md
  // → "Feature Flag Gating".
  const hasPortalManagement = useFeature('portal_management');

  const { data, isLoading } = useBrandingSettings();
  const updateSettings = useUpdateBrandingSettings();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: portalThemeData } = usePortalTheme();
  const updatePortalTheme = useUpdatePortalTheme();
  const portalLogoInputRef = useRef<HTMLInputElement>(null);

  const [portalThemeForm, setPortalThemeForm] = useState({
    primary: DEFAULT_THEME.colors.primary,
    secondary: DEFAULT_THEME.colors.secondary ?? '',
    accent: DEFAULT_THEME.colors.accent ?? '',
    font_family: DEFAULT_THEME.typography.font_family,
    logo_url: '',
    favicon_url: '',
    company_name: '',
  });
  const [showPortalPrimaryPicker, setShowPortalPrimaryPicker] = useState(false);
  const [showPortalSecondaryPicker, setShowPortalSecondaryPicker] = useState(false);
  const [showPortalAccentPicker, setShowPortalAccentPicker] = useState(false);
  const [portalLogoUploading, setPortalLogoUploading] = useState(false);
  const [portalLogoError, setPortalLogoError] = useState<string | null>(null);

  useEffect(() => {
    if (portalThemeData) {
      const t = portalThemeData as TenantTheme;
      setPortalThemeForm({
        primary: t.colors?.primary ?? DEFAULT_THEME.colors.primary,
        secondary: t.colors?.secondary ?? DEFAULT_THEME.colors.secondary ?? '',
        accent: t.colors?.accent ?? DEFAULT_THEME.colors.accent ?? '',
        font_family: t.typography?.font_family ?? 'system-ui',
        logo_url: t.branding?.logo_url ?? '',
        favicon_url: t.branding?.favicon_url ?? '',
        company_name: t.branding?.company_name ?? '',
      });
    }
  }, [portalThemeData]);

  const handlePortalThemeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload: Partial<TenantTheme> = {
      colors: {
        primary: portalThemeForm.primary,
        ...(portalThemeForm.secondary && { secondary: portalThemeForm.secondary }),
        ...(portalThemeForm.accent && { accent: portalThemeForm.accent }),
      },
      typography: { font_family: portalThemeForm.font_family as TenantTheme['typography']['font_family'] },
      branding: {
        ...(portalThemeForm.logo_url && { logo_url: portalThemeForm.logo_url }),
        ...(portalThemeForm.favicon_url && { favicon_url: portalThemeForm.favicon_url }),
        ...(portalThemeForm.company_name && { company_name: portalThemeForm.company_name }),
      },
    };
    await updatePortalTheme.mutateAsync(payload);
  };

  const handlePortalLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPortalLogoError(null);
    if (file.size > 2 * 1024 * 1024) {
      setPortalLogoError(`File too large: ${(file.size / 1024 / 1024).toFixed(1)}MB (max 2MB)`);
      return;
    }
    setPortalLogoUploading(true);
    try {
      const result = await api.settings.uploadThemeLogo(file);
      setPortalThemeForm(prev => ({ ...prev, logo_url: result.url }));
    } catch (err: any) {
      setPortalLogoError(err.message || 'Logo upload failed');
    } finally {
      setPortalLogoUploading(false);
      if (portalLogoInputRef.current) portalLogoInputRef.current.value = '';
    }
  };

  const [formData, setFormData] = useState<Partial<BrandingSettings>>({
    logo_svg: null,
    invoice_logo_svg: null, // Phase 9.18a: Separate invoice logo
    primary_color: '#65339a', // BuildGravity purple default
    cover_image_base64: null, // Phase 10c.9: Default proposal cover image
    signature_image_base64: null, // Phase 10c.9: Signature image
  });
  const invoiceFileInputRef = useRef<HTMLInputElement>(null);
  const [invoiceUploadError, setInvoiceUploadError] = useState<string | null>(null);

  const [showColorPicker, setShowColorPicker] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Initialize form with fetched data
  useEffect(() => {
    if (data) {
      setFormData(data);
    }
  }, [data]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await updateSettings.mutateAsync(formData);
  };

  const handleChange = (field: keyof BrandingSettings, value: string | null) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleColorChange = (color: string) => {
    setFormData(prev => ({
      ...prev,
      primary_color: color,
    }));
  };

  const handleSvgUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadError(null);

    // Validate file type
    if (!file.type.includes('svg')) {
      setUploadError('Please upload an SVG file');
      return;
    }

    // Validate file size (100KB max)
    const maxSize = 100 * 1024; // 100KB
    if (file.size > maxSize) {
      setUploadError(`File too large: ${(file.size / 1024).toFixed(1)}KB (max 100KB)`);
      return;
    }

    try {
      // Read file as text
      const text = await file.text();

      // Basic SVG validation
      const lowerText = text.trim().toLowerCase();
      if (!lowerText.startsWith('<?xml') && !lowerText.startsWith('<svg')) {
        setUploadError('Invalid SVG file: must start with <?xml or <svg tag');
        return;
      }

      if (!lowerText.includes('<svg')) {
        setUploadError('Invalid SVG file: missing <svg> tag');
        return;
      }

      // Convert to base64
      const base64 = btoa(unescape(encodeURIComponent(text)));

      // Update form data
      setFormData(prev => ({
        ...prev,
        logo_svg: base64,
      }));

      setUploadError(null);
    } catch (error) {
      setUploadError('Failed to read SVG file');
      console.error('SVG upload error:', error);
    }
  };

  const handleRemoveLogo = () => {
    setFormData(prev => ({
      ...prev,
      logo_svg: null,
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Phase 9.18a: Invoice logo upload handlers
  const handleInvoiceSvgUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setInvoiceUploadError(null);

    if (!file.type.includes('svg')) {
      setInvoiceUploadError('Please upload an SVG file');
      return;
    }

    const maxSize = 100 * 1024;
    if (file.size > maxSize) {
      setInvoiceUploadError(`File too large: ${(file.size / 1024).toFixed(1)}KB (max 100KB)`);
      return;
    }

    try {
      const text = await file.text();
      const lowerText = text.trim().toLowerCase();
      if (!lowerText.startsWith('<?xml') && !lowerText.startsWith('<svg')) {
        setInvoiceUploadError('Invalid SVG file: must start with <?xml or <svg tag');
        return;
      }
      if (!lowerText.includes('<svg')) {
        setInvoiceUploadError('Invalid SVG file: missing <svg> tag');
        return;
      }

      const base64 = btoa(unescape(encodeURIComponent(text)));
      setFormData(prev => ({ ...prev, invoice_logo_svg: base64 }));
      setInvoiceUploadError(null);
    } catch (error) {
      setInvoiceUploadError('Failed to read SVG file');
      console.error('Invoice SVG upload error:', error);
    }
  };

  const handleRemoveInvoiceLogo = () => {
    setFormData(prev => ({ ...prev, invoice_logo_svg: null }));
    if (invoiceFileInputRef.current) {
      invoiceFileInputRef.current.value = '';
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <>
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 pb-4 border-b">
        <Palette className="w-5 h-5 text-slate-600" />
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Branding & Visual Identity</h2>
          <p className="text-sm text-gray-600 mt-1">
            Customize your brand appearance on invoices and documents
          </p>
        </div>
      </div>

      {/* Form Fields */}
      <div className="space-y-6">
        {/* Primary Color */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Primary Brand Color <span className="text-red-500">*</span>
          </label>

          {/* Color Preview and Input */}
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => setShowColorPicker(!showColorPicker)}
              className="w-16 h-16 rounded-lg border-2 border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all hover:scale-105"
              style={{ backgroundColor: formData.primary_color || '#65339a' }}
              title="Click to change color"
            />

            <div className="flex-1">
              <input
                type="text"
                name="primary_color"
                value={formData.primary_color || ''}
                onChange={(e) => handleChange('primary_color', e.target.value)}
                required
                pattern="^#[0-9A-Fa-f]{6}$"
                maxLength={7}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono"
                placeholder="#65339a"
              />
              <p className="text-xs text-gray-500 mt-1">
                Hex color code (e.g., #65339a for purple, #71cff4 for blue)
              </p>
            </div>
          </div>

          {/* Color Picker Popup */}
          {showColorPicker && (
            <div className="mt-4 p-4 bg-white border border-gray-300 rounded-lg shadow-lg">
              <HexColorPicker
                color={formData.primary_color || '#65339a'}
                onChange={handleColorChange}
              />
              <Button
                type="button"
                onClick={() => setShowColorPicker(false)}
                variant="outline"
                size="sm"
                className="mt-3 w-full"
              >
                Done
              </Button>
            </div>
          )}

          {/* Color Preview Examples */}
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm font-medium text-gray-700 mb-2">Preview</p>
            <div className="flex items-center gap-3">
              <div
                className="px-4 py-2 rounded-lg text-white font-semibold text-sm"
                style={{ backgroundColor: formData.primary_color || '#65339a' }}
              >
                Button
              </div>
              <div
                className="px-4 py-2 rounded-lg text-sm font-semibold"
                style={{
                  backgroundColor: `${formData.primary_color || '#65339a'}20`,
                  color: formData.primary_color || '#65339a'
                }}
              >
                Badge
              </div>
              <div
                className="w-12 h-12 rounded-full"
                style={{ backgroundColor: formData.primary_color || '#65339a' }}
              />
            </div>
          </div>
        </div>

        {/* Proposal Logo Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Proposal Logo (SVG)
          </label>
          <p className="text-xs text-gray-500 mb-3">
            Logo for proposals and fee letters (max 100KB)
          </p>

          {/* Upload Area */}
          {!formData.logo_svg ? (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-purple-400 transition-colors">
              <input
                ref={fileInputRef}
                type="file"
                accept=".svg,image/svg+xml"
                onChange={handleSvgUpload}
                className="hidden"
                id="svg-upload"
              />
              <label
                htmlFor="svg-upload"
                className="cursor-pointer flex flex-col items-center gap-2"
              >
                <Upload className="w-10 h-10 text-gray-400" />
                <span className="text-sm font-medium text-gray-700">
                  Click to upload SVG logo
                </span>
                <span className="text-xs text-gray-500">
                  or drag and drop
                </span>
              </label>
            </div>
          ) : (
            /* Logo Preview */
            <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <ImageIcon className="w-4 h-4 text-gray-600" />
                  <span className="text-sm font-medium text-gray-700">Logo Preview</span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleRemoveLogo}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <X className="w-4 h-4 mr-1" />
                  Remove
                </Button>
              </div>

              {/* SVG Preview */}
              <div className="bg-white p-4 rounded border border-gray-200 flex items-center justify-center min-h-[120px]">
                <img
                  src={`data:image/svg+xml;base64,${formData.logo_svg}`}
                  alt="Logo preview"
                  className="max-w-full max-h-32 object-contain"
                />
              </div>
            </div>
          )}

          {/* Upload Error */}
          {uploadError && (
            <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-700">{uploadError}</p>
            </div>
          )}

          {/* Help Text */}
          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-sky-700">
              <strong>Tip:</strong> SVG logos scale perfectly at any size and work great in PDFs.
              If you don't have an SVG, your company name will be used instead.
            </p>
          </div>
        </div>

        {/* Phase 9.18a: Invoice Logo Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Invoice Logo (SVG)
          </label>
          <p className="text-xs text-gray-500 mb-3">
            Separate logo for invoices. If not set, uses the Proposal Logo above.
          </p>

          {/* Upload Area */}
          {!formData.invoice_logo_svg ? (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-purple-400 transition-colors">
              <input
                ref={invoiceFileInputRef}
                type="file"
                accept=".svg,image/svg+xml"
                onChange={handleInvoiceSvgUpload}
                className="hidden"
                id="invoice-svg-upload"
                data-testid="invoice-logo-upload"
              />
              <label
                htmlFor="invoice-svg-upload"
                className="cursor-pointer flex flex-col items-center gap-2"
              >
                <Upload className="w-10 h-10 text-gray-400" />
                <span className="text-sm font-medium text-gray-700">
                  Click to upload invoice logo
                </span>
                <span className="text-xs text-gray-500">
                  or drag and drop
                </span>
              </label>
            </div>
          ) : (
            /* Invoice Logo Preview */
            <div className="border border-gray-300 rounded-lg p-4 bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <ImageIcon className="w-4 h-4 text-gray-600" />
                  <span className="text-sm font-medium text-gray-700">Invoice Logo Preview</span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleRemoveInvoiceLogo}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid="remove-invoice-logo"
                >
                  <X className="w-4 h-4 mr-1" />
                  Remove
                </Button>
              </div>

              <div className="bg-white p-4 rounded border border-gray-200 flex items-center justify-center min-h-[120px]">
                <img
                  src={`data:image/svg+xml;base64,${formData.invoice_logo_svg}`}
                  alt="Invoice logo preview"
                  className="max-w-full max-h-32 object-contain"
                  data-testid="invoice-logo-preview"
                />
              </div>
            </div>
          )}

          {/* Upload Error */}
          {invoiceUploadError && (
            <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-700">{invoiceUploadError}</p>
            </div>
          )}
        </div>

        {/* Phase 10c.9: Default Proposal Cover Image */}
        <ImageUpload
          value={formData.cover_image_base64 ?? null}
          onChange={(value) => handleChange('cover_image_base64', value)}
          label="Default Proposal Cover Image"
          description="Cover photo for page 1 of proposals. JPEG recommended, resized to max 1200px wide."
          targetMaxWidth={1200}
          maxFileSize={500 * 1024}
          outputFormat="jpeg"
          quality={0.85}
          testId="cover-image-upload"
        />

        {/* Phase 10c.9: Signature Image */}
        <ImageUpload
          value={formData.signature_image_base64 ?? null}
          onChange={(value) => handleChange('signature_image_base64', value)}
          label="Signature Image"
          description="Signature for the proposal cover letter. PNG recommended for transparency, max 400px wide."
          targetMaxWidth={400}
          maxFileSize={100 * 1024}
          outputFormat="png"
          testId="signature-image-upload"
        />
      </div>

      {/* Submit Button */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t">
        <Button
          type="submit"
          disabled={updateSettings.isPending || !formData.primary_color}
          className="inline-flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          {updateSettings.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </form>

    {/* ── Portal Theme ── only visible with portal_management flag ── */}
    {hasPortalManagement && (
      <form onSubmit={handlePortalThemeSubmit} className="space-y-6 mt-8 pt-8 border-t">
        <div className="flex items-center gap-3 pb-4 border-b">
          <Globe className="w-5 h-5 text-slate-600" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Portal Theme</h2>
            <p className="text-sm text-gray-600 mt-1">
              Customize branding for your client-facing portal.
            </p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Primary Color */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Primary Color <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => setShowPortalPrimaryPicker(!showPortalPrimaryPicker)}
                className="w-12 h-12 rounded-lg border-2 border-gray-300 flex-shrink-0"
                style={{ backgroundColor: portalThemeForm.primary }}
                title="Click to change"
              />
              <input
                type="text"
                value={portalThemeForm.primary}
                onChange={(e) => setPortalThemeForm(p => ({ ...p, primary: e.target.value }))}
                required
                pattern="^#[0-9A-Fa-f]{6}$"
                maxLength={7}
                className="w-32 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                placeholder="#2563eb"
              />
            </div>
            {showPortalPrimaryPicker && (
              <div className="mt-3 p-4 bg-white border border-gray-300 rounded-lg shadow-lg inline-block">
                <HexColorPicker
                  color={portalThemeForm.primary}
                  onChange={(c) => setPortalThemeForm(p => ({ ...p, primary: c }))}
                />
                <Button type="button" variant="outline" size="sm" className="mt-3 w-full"
                  onClick={() => setShowPortalPrimaryPicker(false)}>Done</Button>
              </div>
            )}
          </div>

          {/* Secondary + Accent */}
          <div className="grid grid-cols-2 gap-4">
            {[
              { key: 'secondary', label: 'Secondary Color', show: showPortalSecondaryPicker, setShow: setShowPortalSecondaryPicker, placeholder: '#7c3aed' },
              { key: 'accent', label: 'Accent Color', show: showPortalAccentPicker, setShow: setShowPortalAccentPicker, placeholder: '#06b6d4' },
            ].map(({ key, label, show, setShow, placeholder }) => (
              <div key={key}>
                <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setShow(!show)}
                    className="w-10 h-10 rounded-lg border-2 border-gray-300 flex-shrink-0"
                    style={{ backgroundColor: (portalThemeForm as Record<string, string>)[key] || placeholder }}
                  />
                  <input
                    type="text"
                    value={(portalThemeForm as Record<string, string>)[key]}
                    onChange={(e) => setPortalThemeForm(p => ({ ...p, [key]: e.target.value }))}
                    pattern="^#[0-9A-Fa-f]{6}$"
                    maxLength={7}
                    className="flex-1 px-2 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                    placeholder={placeholder}
                  />
                </div>
                {show && (
                  <div className="mt-2 p-3 bg-white border border-gray-300 rounded-lg shadow-lg inline-block">
                    <HexColorPicker
                      color={(portalThemeForm as Record<string, string>)[key] || placeholder}
                      onChange={(c) => setPortalThemeForm(p => ({ ...p, [key]: c }))}
                    />
                    <Button type="button" variant="outline" size="sm" className="mt-2 w-full"
                      onClick={() => setShow(false)}>Done</Button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Font Family */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Font Family</label>
            <select
              value={portalThemeForm.font_family}
              onChange={(e) => setPortalThemeForm(p => ({ ...p, font_family: e.target.value }))}
              className="w-48 px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              {FONT_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Company Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
            <input
              type="text"
              value={portalThemeForm.company_name}
              onChange={(e) => setPortalThemeForm(p => ({ ...p, company_name: e.target.value }))}
              maxLength={200}
              className="w-full max-w-sm px-3 py-2 border border-gray-300 rounded-lg text-sm"
              placeholder="Your company name"
            />
            <p className="text-xs text-gray-500 mt-1">Shown in the portal header</p>
          </div>

          {/* Portal Logo Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Portal Logo</label>
            <p className="text-xs text-gray-500 mb-3">SVG, PNG, or JPEG — max 2MB.</p>
            <div className="flex items-center gap-3 flex-wrap">
              <input
                ref={portalLogoInputRef}
                type="file"
                accept=".svg,.png,.jpg,.jpeg,image/svg+xml,image/png,image/jpeg"
                onChange={handlePortalLogoUpload}
                className="hidden"
                id="portal-logo-upload"
              />
              <label
                htmlFor="portal-logo-upload"
                className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm cursor-pointer hover:bg-gray-50"
              >
                <Upload className="w-4 h-4" />
                {portalLogoUploading ? 'Uploading...' : 'Upload Logo'}
              </label>
              {portalThemeForm.logo_url && (
                <div className="flex items-center gap-2">
                  <img
                    src={portalThemeForm.logo_url}
                    alt="Portal logo"
                    className="h-8 w-auto object-contain border border-gray-200 rounded"
                  />
                  <button
                    type="button"
                    onClick={() => setPortalThemeForm(p => ({ ...p, logo_url: '' }))}
                    className="text-red-600 hover:text-red-700"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
            {portalLogoError && (
              <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-700">{portalLogoError}</p>
              </div>
            )}
          </div>

          {/* Favicon URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Favicon URL</label>
            <p className="text-xs text-gray-500 mb-2">HTTPS URL to .ico, .png, or .svg favicon. Shown in browser tab.</p>
            <input
              type="url"
              value={portalThemeForm.favicon_url}
              onChange={(e) => setPortalThemeForm(p => ({ ...p, favicon_url: e.target.value }))}
              pattern="https://.*\.(ico|png|svg)(\?.*)?"
              className="w-full max-w-sm px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
              placeholder="https://example.com/favicon.png"
            />
          </div>

          {/* Live Preview */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Preview</label>
            <div className="rounded-lg border border-gray-200 overflow-hidden">
              <div
                className="flex items-center gap-3 px-4 py-3"
                style={{ backgroundColor: portalThemeForm.primary }}
              >
                {portalThemeForm.logo_url && (
                  <img src={portalThemeForm.logo_url} alt="" className="h-6 w-auto object-contain" />
                )}
                <span className="font-semibold text-sm text-white">
                  {portalThemeForm.company_name || 'Company Name'}
                </span>
              </div>
              <div className="bg-white p-4 flex gap-3 items-center flex-wrap">
                {[
                  { key: 'primary', label: 'Primary' },
                  { key: 'secondary', label: 'Secondary' },
                  { key: 'accent', label: 'Accent' },
                ].map(({ key, label }) => (
                  <div key={key} className="text-center">
                    <div
                      className="h-8 w-8 rounded-md border border-gray-200 mx-auto"
                      style={{ backgroundColor: (portalThemeForm as Record<string, string>)[key] || '#e5e7eb' }}
                    />
                    <p className="text-xs text-gray-500 mt-1">{label}</p>
                  </div>
                ))}
                <button
                  type="button"
                  className="ml-4 px-4 py-2 rounded-md text-sm font-medium text-white"
                  style={{ backgroundColor: portalThemeForm.primary }}
                >
                  Button
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pt-4 border-t">
          <Button
            type="submit"
            disabled={updatePortalTheme.isPending || !portalThemeForm.primary}
            className="inline-flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {updatePortalTheme.isPending ? 'Saving...' : 'Save Portal Theme'}
          </Button>
        </div>
      </form>
    )}
    </>
  );
}
